// src/components/ChecklistOnboarding.js
// Un unico motore, quattro contenuti diversi a seconda di chi apre Nexiora — titolare/admin,
// Amministrazione, PM, Operativi. Ognuno vede solo la propria, mai più di una insieme.
// Magazziniere e Gestione aziendale non hanno ancora un percorso dedicato — nessuna
// checklist per loro, come prima di questa modifica.
import React, { useState, useEffect } from "react";
import { collection, query, where, getDocs, getDoc, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { ChevronDown, ChevronUp, CheckCircle, Circle, X } from "lucide-react";

// ── TITOLARE / ADMIN — configurazione iniziale dell'azienda ────────────────────────────
const PASSI_TITOLARE = [
  {
    id: "impostazioni", n: 1, titolo: "Configura i dati aziendali",
    desc: "Inserisci ragione sociale, P.IVA, indirizzo e carica il logo aziendale. Questi dati appaiono in tutti i PDF.",
    dove: "Sidebar → Configurazione → Impostazioni aziendali", pagina: "impostazioni-org", urgente: true,
    check: async (orgId) => {
      const snap = await getDoc(doc(db, "organizations", orgId));
      if (!snap.exists()) return false;
      const d = snap.data();
      return !!(d.nome || d.name) && !!d.piva && !!d.indirizzo;
    }
  },
  {
    id: "clienti", n: 2, titolo: "Aggiungi almeno un cliente",
    desc: "Hai già un elenco clienti? Importalo da Excel in pochi minuti. Parti da zero? Aggiungili uno alla volta — serve la P.IVA o, per i privati, il codice fiscale; Nexiora controlla i duplicati da sola.",
    dove: "Sidebar → Gestione → Clienti", pagina: "clienti", urgente: true,
    check: async (orgId) => (await getDocs(query(collection(db, "clienti"), where("organizationId", "==", orgId)))).size >= 1
  },
  {
    id: "commessa", n: 3, titolo: "Crea la tua prima commessa",
    desc: "Il passo più veloce per vedere Nexiora all'opera davvero: collegala al cliente appena aggiunto e assegna te stesso come responsabile — non serve aver già invitato nessuno. In pochi minuti hai qualcosa di vivo da guardare, non solo campi compilati.",
    dove: "Sidebar → Progetti → + (aggiungi commessa)", pagina: "new-project", urgente: true,
    check: async (orgId) => {
      const snap = await getDocs(query(collection(db, "projects"), where("organizationId", "==", orgId)));
      return snap.docs.some(d => !d.data().isParent);
    }
  },
  {
    id: "fonteOre", n: 4, titolo: "Imposta la Fonte Ore per il Controllo Economico",
    desc: "Senza questa scelta, Controllo Economico non saprà mai da dove prendere le ore di manodopera — costi e margini resteranno incompleti su ogni commessa, anche con dati corretti dappertutto. Scegli tra Programmazione Settimanale, Rapportini o Presenze: quella che già usi per tracciare le ore lavorate.",
    dove: "Sidebar → Configurazione → Impostazioni aziendali → Controllo Economico — Fonte ore", pagina: "impostazioni-org", urgente: true,
    check: async (orgId) => {
      const snap = await getDoc(doc(db, "organizations", orgId));
      return !!(snap.exists() && snap.data().fontOreEconomico);
    }
  },
  {
    id: "dipendenti", n: 5, titolo: "Inserisci i dipendenti",
    desc: "Può aspettare — non serve per creare commesse. Hai già un elenco? Importalo da Excel in pochi minuti. Parti da zero? Aggiungili uno alla volta — ruolo e costo orario sono obbligatori, servono per il calcolo dei costi reali.",
    dove: "Sidebar → Gestione → Dipendenti", pagina: "dipendenti", urgente: false,
    check: async (orgId) => (await getDocs(query(collection(db, "dipendenti"), where("organizationId", "==", orgId)))).size >= 1
  },
  {
    id: "listino", n: 6, titolo: "Listino Materiali (opzionale)",
    desc: "Hai già un listino fornitore? Importalo da Excel. Utile per compilare offerte e rientri materiali più in fretta — ma puoi saltarlo e aggiungerlo quando vuoi.",
    dove: "Sidebar → Acquisti → Listino Materiali", pagina: "listino-materiali", urgente: false,
    check: async (orgId) => (await getDocs(query(collection(db, "listinoMateriali"), where("organizationId", "==", orgId)))).size >= 1
  },
  {
    id: "ruoli", n: 7, titolo: "Assegna un ruolo a chi hai già invitato",
    desc: "Ogni persona invitata ha bisogno di un ruolo — determina cosa vede subito, senza dover configurare nulla a mano.",
    dove: "Sidebar → Gestione → Gestione Utenti", pagina: "users", urgente: false,
    check: async (orgId) => {
      const snap = await getDocs(query(collection(db, "users"), where("organizationId", "==", orgId), where("enabled","==",true)));
      // Il titolare non ha bisogno di un ruolo operativo (vede tutto): conta chi è stato invitato.
      const invitati = snap.docs.filter(d => d.data().role !== "titolare");
      return invitati.length >= 1 && invitati.every(d => !!d.data().ruoloOperativo);
    }
  },
  {
    id: "abilitazioni", n: 8, titolo: "Rifinisci le abilitazioni, se serve",
    desc: "I ruoli hanno già permessi di partenza sensati. Qui puoi personalizzarli, per ruolo o per singola persona.",
    dove: "Sidebar → Configurazione → Abilitazioni", pagina: "abilitazioni", urgente: false,
    // Le abilitazioni dell'azienda sono un solo documento con l'id dell'organizzazione
    // (come le legge la barra laterale): prima si cercava un campo che quel documento non ha.
    check: async (orgId) => (await getDoc(doc(db, "abilitazioniSezioni", orgId))).exists()
  },
  {
    id: "utenti", n: 9, titolo: "Invita il team",
    desc: "Aggiungi i tuoi collaboratori come utenti Nexiora. Potranno accedere da browser o smartphone.",
    dove: "Sidebar → Gestione → Gestione Utenti", pagina: "users", urgente: false,
    check: async (orgId) => (await getDocs(query(collection(db, "users"), where("organizationId", "==", orgId)))).size >= 2
  },
];

// ── AMMINISTRAZIONE — esplorativi, nessun dato da creare per completarli ───────────────
const PASSI_AMMINISTRAZIONE = [
  {
    id: "scadenziario", n: 1, titolo: "Dai un'occhiata allo Scadenziario",
    desc: "Visure, rinnovi, adempimenti — è il posto dove Nexiora ti avvisa prima che diventino urgenti.",
    dove: "Sidebar → Acquisti → Scadenzario", pagina: "scadenziario", urgente: true, manuale: true,
  },
  {
    id: "cartellini", n: 2, titolo: "Controlla se ci sono Cartellini da approvare",
    desc: "Chi lavora nel campo invia il proprio cartellino a fine periodo — qui li rivedi e li approvi.",
    dove: "Sidebar → Gestione → Approvazione Cartellini", pagina: "approvazione-cartellini", urgente: false, manuale: true,
  },
  {
    id: "notespese", n: 3, titolo: "Controlla se ci sono Note Spese da approvare",
    desc: "Stesso principio dei cartellini — ricorda che nessuno, nemmeno un amministratore, può approvare una propria nota spesa: serve sempre un'altra persona.",
    dove: "Sidebar → Gestione → Approvazione Note Spese", pagina: "approvazione-note-spese", urgente: false, manuale: true,
  },
  {
    id: "rapportini", n: 4, titolo: "Esplora il Registro Rapportini (opzionale)",
    desc: "L'elenco di tutti i rapporti di intervento emessi dall'azienda, in un unico posto.",
    dove: "Sidebar → Registri → Registro rapportini", pagina: "registro-rapportini", urgente: false, manuale: true,
  },
];

// ── PM — esplorativi, orientati agli strumenti di sintesi ──────────────────────────────
const PASSI_PM = [
  {
    id: "lemiecommesse", n: 1, titolo: "Guarda le commesse di cui sei responsabile",
    desc: "Importi, fatturato, saldo — tutto in una tabella, solo le tue.",
    dove: "Sidebar → Personale → Le mie commesse", pagina: "le-mie-commesse", urgente: true, manuale: true,
  },
  {
    id: "cabinaregia", n: 2, titolo: "Apri la Cabina di Regia di una tua commessa",
    desc: "Come sta davvero quella commessa, in pochi secondi — dentro la pagina della commessa stessa, sempre visibile.",
    dove: "Apri una commessa → tab Cabina di Regia", pagina: "le-mie-commesse", urgente: false, manuale: true,
  },
  {
    id: "centrooperativo", n: 3, titolo: "Dai un'occhiata al Centro Operativo",
    desc: "La vista d'insieme su tutte le commesse insieme — scadenze, ore, margini a rischio.",
    dove: "Sidebar → Direzione → Centro Operativo", pagina: "control-center", urgente: false, manuale: true,
  },
  {
    id: "programmazione", n: 4, titolo: "Esplora Programmazione settimanale (opzionale)",
    desc: "Chi lavora dove, questa settimana — utile per organizzare il personale in anticipo.",
    dove: "Sidebar → Personale → Programmazione", pagina: "programmazione", urgente: false, manuale: true,
  },
];

// ── OPERATIVI — non configurazione, comprensione di come funzionano due cose che
// contano davvero per loro ogni giorno: il cartellino e la nota spese. Qui gli passi
// verificabili con un vero dato (hai già fatto la tua prima volta), non solo esplorativi.
const PASSI_OPERATIVI = [
  {
    id: "cartellino", n: 1, titolo: "Compila il tuo primo cartellino presenze",
    desc: "Le ore che registri qui alimentano automaticamente i costi delle commesse — un errore per distrazione si riflette dritto nei numeri reali dell'azienda. Vale la pena compilarlo con cura, ogni volta.",
    dove: "Sidebar → Personale → Presenze", pagina: "presenze", urgente: true,
    check: async (orgId, uid) => (await getDocs(query(collection(db, "presenze"), where("uid","==",uid), where("orgId","==",orgId)))).size >= 1,
  },
  {
    id: "notaspese", n: 2, titolo: "Invia la tua prima Nota Spese",
    desc: "Una volta approvata da un amministratore, non potrai più modificarla. E ricorda: nessuno può approvare una propria nota spesa, nemmeno un amministratore — serve sempre un'altra persona. È una regola di correttezza, non un limite tecnico.",
    dove: "Sidebar → Personale → Nota Spese", pagina: "nota-spese", urgente: false,
    // Conta solo una nota davvero inviata: la bozza nasce da sola appena si scrive una spesa.
    check: async (orgId, uid) => (await getDocs(query(collection(db, "noteSpese"), where("uid","==",uid), where("orgId","==",orgId)))).docs.some(d => (d.data().stato || "bozza") !== "bozza"),
  },
];

function ruoloAttivo({ isOrgAdmin, isPlatformOwner, ruoloOperativo }) {
  if (isOrgAdmin || isPlatformOwner) return { chiave: "titolare", passi: PASSI_TITOLARE, titolo: "🚀 Configura Nexiora" };
  if (ruoloOperativo === "amministrazione") return { chiave: "amministrazione", passi: PASSI_AMMINISTRAZIONE, titolo: "🗂️ Per iniziare — Amministrazione" };
  if (ruoloOperativo === "pm") return { chiave: "pm", passi: PASSI_PM, titolo: "🎛️ Per iniziare — Project Manager" };
  if (ruoloOperativo === "operativi") return { chiave: "operativi", passi: PASSI_OPERATIVI, titolo: "👷 Le prime due cose da sapere" };
  return null;
}

export default function ChecklistOnboarding({ onNavigate }) {
  const { currentUser, userProfile, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const [stati, setStati] = useState({});
  const [loading, setLoading] = useState(true);
  const [aperta, setAperta] = useState(true);
  const [nascosta, setNascosta] = useState(null); // null finché non sappiamo se era già stata chiusa
  const [mostraCompletati, setMostraCompletati] = useState(false);
  const [manualiCompletati, setManualiCompletati] = useState([]);
  // Passaggio di mano dal modale di benvenuto — vale solo per il primo caricamento subito
  // dopo averlo chiuso, mai più dopo (letto e rimosso subito da sessionStorage).
  const [passaggioDiMano] = useState(() => {
    const presente = sessionStorage.getItem("nexiora_appena_chiuso_onboarding") === "1";
    if (presente) sessionStorage.removeItem("nexiora_appena_chiuso_onboarding");
    return presente;
  });

  const contesto = ruoloAttivo({ isOrgAdmin, isPlatformOwner, ruoloOperativo: userProfile?.ruoloOperativo });

  // Stato persistente — chiusura e passi manuali si ricordano per davvero, non solo per la sessione
  useEffect(() => {
    if (!currentUser?.uid || !contesto) return;
    getDoc(doc(db, "onboardingStato", currentUser.uid)).then(snap => {
      if (snap.exists()) {
        const d = snap.data();
        setNascosta(d.nascosta?.[contesto.chiave] || false);
        setManualiCompletati(d.manualiCompletati?.[contesto.chiave] || []);
      } else {
        setNascosta(false);
      }
    });
  }, [currentUser, contesto?.chiave]);

  useEffect(() => {
    if (!effectiveOrgId || !contesto) return;
    const verifica = async () => {
      setLoading(true);
      const risultati = {};
      await Promise.all(contesto.passi.map(async p => {
        try {
          if (p.manuale) { risultati[p.id] = manualiCompletati.includes(p.id); return; }
          risultati[p.id] = await p.check(effectiveOrgId, currentUser?.uid);
        } catch { risultati[p.id] = false; }
      }));
      setStati(risultati);
      setLoading(false);
    };
    verifica();
  }, [effectiveOrgId, contesto?.chiave, manualiCompletati]);

  if (!contesto) return null;

  const salvaStato = async (campo, valore) => {
    await setDoc(doc(db, "onboardingStato", currentUser.uid), {
      uid: currentUser.uid,
      [campo]: { [contesto.chiave]: valore },
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  const chiudi = () => { setNascosta(true); salvaStato("nascosta", true); };

  const segnaManuale = (passoId) => {
    const nuovi = manualiCompletati.includes(passoId) ? manualiCompletati.filter(x=>x!==passoId) : [...manualiCompletati, passoId];
    setManualiCompletati(nuovi);
    salvaStato("manualiCompletati", nuovi);
  };

  const completati = contesto.passi.filter(p => stati[p.id]).length;
  const totale = contesto.passi.length;
  const percento = totale ? Math.round((completati / totale) * 100) : 0;
  const tuttiCompletati = completati === totale;

  if (nascosta === null || nascosta || (tuttiCompletati && !loading)) return null;

  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14,
      marginBottom: 20, overflow: "hidden",
      boxShadow: "0 2px 8px rgba(0,0,0,.06)"
    }}>
      <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", background: "#faf5ff", borderBottom: aperta ? "1px solid #e5e7eb" : "none" }}
        onClick={() => setAperta(!aperta)}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#1e1b4b" }}>{contesto.titolo}</span>
            <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 10, background: percento === 100 ? "#d1fae5" : "#ede9fe", color: percento === 100 ? "#065f46" : "#7c3aed", fontWeight: 700 }}>
              {completati}/{totale} completati
            </span>
          </div>
          <div style={{ height: 6, background: "#e5e7eb", borderRadius: 10, overflow: "hidden" }}>
            <div style={{
              height: 6, borderRadius: 10,
              background: percento === 100 ? "#059669" : "linear-gradient(90deg,#7c3aed,#a78bfa)",
              width: `${percento}%`, transition: "width .5s ease"
            }} />
          </div>
          <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 3 }}>{percento}% completato</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <div style={{ padding: 4, borderRadius: 6, background: "rgba(124,58,237,.08)" }}>
            {aperta ? <ChevronUp size={15} color="#7c3aed" /> : <ChevronDown size={15} color="#7c3aed" />}
          </div>
          <button onClick={e => { e.stopPropagation(); chiudi(); }}
            title="Nascondi — non tornerà più a comparire"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 4, borderRadius: 6, display:"flex" }}
            onMouseOver={e=>{e.currentTarget.style.background="#f3f4f6"; e.currentTarget.style.color="#6b7280";}}
            onMouseOut={e=>{e.currentTarget.style.background="none"; e.currentTarget.style.color="#9ca3af";}}>
            <X size={15} />
          </button>
        </div>
      </div>

      {aperta && (
        <div style={{ padding: "8px 0" }}>
          {contesto.chiave === "titolare" && (
            <div style={{ margin: "10px 18px", padding: "9px 12px", borderRadius: 8,
              border: "1px solid #7c3aed", background: "#f5f3ff", color: "#5b21b6",
              fontSize: 11.5, lineHeight: 1.4 }}>
              💜 Nexiora — I passaggi indicati qui sotto riguardano le impostazioni aziendali,
              visibili e modificabili solo da chi è abilitato alla gestione dell'organizzazione.
            </div>
          )}
          {loading ? (
            <div style={{ padding: "16px 18px", fontSize: 12, color: "#9ca3af" }}>Verifica configurazione...</div>
          ) : (
            <>
              {percento >= 50 && completati > 0 && !mostraCompletati && (
                <div onClick={()=>setMostraCompletati(true)}
                  style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 18px", cursor:"pointer",
                    borderBottom:"1px solid #f9fafb", color:"#059669" }}
                  onMouseOver={e=>e.currentTarget.style.background="#f9fafb"}
                  onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                  <CheckCircle size={16} color="#059669"/>
                  <span style={{ fontSize:12, fontWeight:600 }}>{completati} passaggi{completati===1?"o":""} già completat{completati===1?"o":"i"}</span>
                  <span style={{ fontSize:11, color:"#9ca3af", marginLeft:"auto" }}>Mostra ↓</span>
                </div>
              )}
              {percento >= 50 && mostraCompletati && (
                <div onClick={()=>setMostraCompletati(false)}
                  style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 18px", cursor:"pointer", color:"#9ca3af" }}>
                  <span style={{ fontSize:11, marginLeft:"auto" }}>Nascondi i completati ↑</span>
                </div>
              )}
              {contesto.passi.filter(p => !stati[p.id] || percento < 50 || mostraCompletati).map((p, i, arr) => {
                const done = stati[p.id];
                const primoNonCompletato = contesto.passi.find(pp => !stati[pp.id]);
                const evidenziato = passaggioDiMano && !done && primoNonCompletato?.id === p.id;
                return (
                  <div key={p.id} style={{
                    display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 18px",
                    borderBottom: i < arr.length - 1 ? "1px solid #f9fafb" : "none",
                    opacity: done ? 0.6 : 1,
                    background: evidenziato ? "#f5f3ff" : !done && p.urgente ? "#fffbeb" : "#fff",
                    boxShadow: evidenziato ? "inset 3px 0 0 #7c3aed" : "none",
                    animation: evidenziato ? "nx-pulso 1.6s ease-in-out 2" : "none",
                  }}>
                    <div style={{ flexShrink: 0, marginTop: 2, cursor: p.manuale?"pointer":"default" }} onClick={()=>p.manuale && segnaManuale(p.id)}>
                      {done
                        ? <CheckCircle size={18} color="#059669" />
                        : <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${p.urgente ? "#d97706" : "#d1d5db"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <span style={{ fontSize: 9, fontWeight: 800, color: p.urgente ? "#d97706" : "#9ca3af" }}>{p.n}</span>
                          </div>
                      }
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                        <span style={{ fontSize: 12, fontWeight: done ? 500 : 700, color: done ? "#9ca3af" : "#111827", textDecoration: done ? "line-through" : "none" }}>
                          {p.titolo}
                        </span>
                        {!done && p.urgente && (
                          <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: "#fef3c7", color: "#d97706", fontWeight: 700, flexShrink: 0 }}>
                            Prima cosa
                          </span>
                        )}
                      </div>
                      {!done && (
                        <>
                          <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.4, marginBottom: 3 }}>{p.desc}</div>
                          <div style={{ fontSize: 10, color: "#7c3aed", fontWeight: 600 }}>📍 {p.dove}</div>
                        </>
                      )}
                    </div>
                    {!done && onNavigate && (
                      <button onClick={()=>onNavigate({page:p.pagina})}
                        style={{ flexShrink:0, alignSelf:"center", padding:"6px 12px", border:"none", borderRadius:7, background:"#7c3aed", color:"#fff", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif", whiteSpace:"nowrap" }}>
                        Vai →
                      </button>
                    )}
                    {p.manuale && !done && (
                      <button onClick={()=>segnaManuale(p.id)} title="Segna come fatto"
                        style={{ flexShrink:0, alignSelf:"center", padding:"6px 8px", border:"1px solid #e5e7eb", borderRadius:7, background:"#fff", color:"#6b7280", fontSize:11, cursor:"pointer" }}>
                        <Circle size={13}/>
                      </button>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
      {passaggioDiMano && <style>{`@keyframes nx-pulso { 0%,100% { background-color: #f5f3ff; } 50% { background-color: #ede9fe; } }`}</style>}
    </div>
  );
}
